import { InputAdornment, InputBaseComponentProps, Box } from '@mui/material';
import React, { useCallback, useMemo, ChangeEvent, ReactElement } from 'react';

import { USFlagIcon } from './icons';
import { FormikTextField } from '../formik-text-field';
import { makeNumberFormatInput } from '../number-format-field';

type FormikTextFieldProps = React.ComponentProps<typeof FormikTextField>;
type Props = Omit<FormikTextFieldProps, 'type'>;

const FormikPhoneField = (props: Props): ReactElement => {
  const { InputProps, disabled, readOnly, formik, name, label, ...rest } = props;
  const setFieldValue = formik.setFieldValue;
  const PhoneNumberFormatInput = useMemo(() => {
    return makeNumberFormatInput(
      {
        format: '+# (###) ###-####',
        mask: '_',
      },
      ({ formattedValue }) => {
        void setFieldValue(name, formattedValue);
      },
    );
  }, [setFieldValue, name]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value.startsWith('+1')) {
        void setFieldValue(name, `+1${e.target.value}`);
      }
    },
    [setFieldValue, name],
  );

  const inputProps: typeof InputProps = useMemo(
    () => ({
      ...InputProps,
      inputComponent: PhoneNumberFormatInput as React.ElementType<InputBaseComponentProps>,
      startAdornment: (
        <InputAdornment position="start">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <USFlagIcon width="40" />
          </Box>
        </InputAdornment>
      ),
    }),
    [InputProps, PhoneNumberFormatInput],
  );

  return (
    <>
      <FormikTextField
        {...rest}
        name={name}
        formik={formik}
        onChange={onChange}
        InputProps={inputProps}
        disabled={disabled}
        label={label}
        readOnly={readOnly}
        type="tel"
      />
    </>
  );
};

export { FormikPhoneField };
